import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Stack, Typography, useTheme, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getCountry } from '../../../Services/Global';
import { sidebarStyles, sidebarCssString } from './SidebarStyles';

// Import icons
import Menu from '../../../Assets/SVG/menu.svg';
import Settings from '../../../Assets/SVG/Service_Settings.svg';
import feedback from '../../../Assets/SVG/Support.svg';
import HygienicIcon from  '../../../Assets/SVG/Food.svg';
import IndustrialIcon from  '../../../Assets/SVG/Industry.svg';
import About from "../../../Assets/SVG/Respect.svg";

// Configuration constants
const FEEDBACK_EMAIL = 'AIDesignWizardFeedback@alfalaval.com';
const FEEDBACK_SUBJECT = '[EMELIE][Feedback] - AI design wizard user feedback';


/**
 *  Sidebar component
 *
 * @param {SidebarProps} props Component properties
 * @return {React.ReactElement} The sidebar component
 */
export const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [isHygienicExpanded, setIsHygienicExpanded] = useState(false);
  const [isIndustrialExpanded, setIsIndustrialExpanded] = useState(false);
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Force re-render when location.search changes to pick up country updates from useCountrySync
  // This ensures Sidebar re-renders when country in URL changes and localStorage is updated
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    // Trigger re-render when URL changes (useCountrySync will update localStorage)
    forceUpdate(prev => prev + 1);
  }, [location.search]);

  // Helper function to build product design URL with dynamic country
  // Always reads fresh from localStorage to ensure we use the latest country value
  // This ensures country from URL (synced by useCountrySync) is always used
  const buildProductDesignUrl = useCallback((process: string, productline: string): string => {
    const encodedProcess = encodeURIComponent(process);
    const encodedProductline = encodeURIComponent(productline);
    // Always read fresh from localStorage - this is the single source of truth
    // useCountrySync ensures localStorage always matches country from URL
    const currentCountry = getCountry() || 'XX';
    return `/productdesign?process=${encodedProcess}&productline=${encodedProductline}&country=${currentCountry}`;
  }, []); // No dependencies - always reads fresh from localStorage

  // Parse current URL query parameters
  const currentQueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    // Always read fresh from localStorage for country (single source of truth)
    const currentCountry = getCountry() || 'XX';
    return {
      process: params.get('process') || '',
      productline: params.get('productline') || '',
      country: params.get('country') || currentCountry
    };
  }, [location.search]);

  // Helper function to check if a product design link is active
  const isProductDesignLinkActive = useCallback((linkUrl: string): boolean => {
    // Only check if we're on the productdesign route
    if (location.pathname !== '/productdesign') {
      return false;
    }

    try {
      const linkUrlObj = new URL(linkUrl, window.location.origin);
      const linkParams = new URLSearchParams(linkUrlObj.search);
      
      // Normalize and compare process, productline, and country
      const linkProcess = decodeURIComponent(linkParams.get('process') || '').toLowerCase().trim();
      const linkProductline = (linkParams.get('productline') || '').toLowerCase().trim();
      const linkCountry = (linkParams.get('country') || '').toUpperCase().trim();
      
      const currentProcess = decodeURIComponent(currentQueryParams.process).toLowerCase().trim();
      const currentProductline = currentQueryParams.productline.toLowerCase().trim();
      const currentCountry = currentQueryParams.country.toUpperCase().trim();

      return (
        linkProcess === currentProcess &&
        linkProductline === currentProductline &&
        linkCountry === currentCountry
      );
    } catch (error) {
      // If URL parsing fails, fall back to simple string comparison
      return location.search === linkUrl.split('?')[1];
    }
  }, [location.pathname, location.search, currentQueryParams]);

  const handleToggle = useCallback(() => {
    setIsOpen(prevOpen => !prevOpen);
  }, []);

  // Update global CSS variable for sidebar width
  useEffect(() => {
    const width = isOpen ? '240px' : '65px';
    document.documentElement.style.setProperty('--sidebar-width', width);
  }, [isOpen]);

  const handleHygienicToggle = useCallback(() => {
    if(!isOpen) handleToggle();
    setIsHygienicExpanded(prev => !prev);
  }, [isOpen, handleToggle]);

  const handleIndustrialToggle = useCallback(() => {
    if(!isOpen) handleToggle();
    setIsIndustrialExpanded(prev => !prev);
  }, [isOpen, handleToggle]);

  const handleFeedbackClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.location.href = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(FEEDBACK_SUBJECT)}`;
  }, []);


  // Close expanded sections when sidebar is collapsed
  useEffect(() => {
    if (!isOpen) {
      setIsHygienicExpanded(false);
      setIsIndustrialExpanded(false);
    }
  }, [isOpen]);

  // Auto-redirect to /about when on home page
  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/about', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Auto-expand Hygienic section when on productdesign page with hygienic productline
  useEffect(() => {
    if (location.pathname === '/productdesign') {
      const params = new URLSearchParams(location.search);
      const productline = (params.get('productline') || '').toLowerCase();
      if (productline === 'hygienic') {
        setIsHygienicExpanded(true);
      } else if (productline === 'industrial') {
        setIsIndustrialExpanded(true);
      }
    }
  }, [location.pathname, location.search]);

  return (
    <>
      <style>{sidebarCssString}</style>
      
      <Stack
        sx={{
          ...sidebarStyles.container(theme, isOpen),
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: theme.shadows[2],
          borderRight: '1px solid',
          borderColor: 'divider'
        }}
        spacing={0}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            scrollbarWidth: 'thin', // Firefox
            scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent', // Firefox: thumb track
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '3px',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
        >
        {/* Menu toggle button */}
        <Box 
          className="sidebar-item" 
          onClick={handleToggle}
          component="div"
        >
          <Box
            component="img"
            src={Menu}
            alt="Menu"
            className="sidebar-icon"
          />
        </Box>

{/* About link */}
<NavLink
                to="/about"
                className={({ isActive }: { isActive: boolean }) =>
                  isActive ? "active sidebar-item" : "link sidebar-item"
                }
              >
                <Box
                  component="img"
                  src={About}
                  alt="Design List"
                  className="sidebar-icon"
                />
                <Typography
                  className="sidebar-text"
                  component="div"
                  sx={sidebarStyles.sidebarText(isOpen)}
                >
                  Home
                </Typography>
              </NavLink>
        {/* Hygienic expandable section */}
        <Box>
          <Box 
            className="sidebar-item" 
            onClick={handleHygienicToggle}
            component="div"
            sx={{ cursor: 'pointer' }}
          >
            <Box
              component="img"
              src={HygienicIcon}
              alt="Hygienic"
              className="sidebar-icon"
            />
            <Typography
              component="div"
              className="sidebar-text" 
              sx={sidebarStyles.sidebarText(isOpen)}
            >
              Hygienic
            </Typography>
            {isOpen && (
              <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                {isHygienicExpanded ? (
                  <ExpandLessIcon sx={{ fontSize: '16px', color: '#323130' }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: '16px', color: '#323130' }} />
                )}
              </Box>
            )}
          </Box>

          <Collapse in={isHygienicExpanded && isOpen}>
            <Box sx={{ backgroundColor: '#fafafa' }}>
              {/* Hygienic sub-section */}
              <Box sx={{ paddingTop: '4px' }}>
                
                {/* Beer Cooling link */}
                <NavLink
                  to={buildProductDesignUrl('Beer Cooling', 'Hygienic')}
                  className={() => 
                    isProductDesignLinkActive(buildProductDesignUrl('Beer Cooling', 'Hygienic')) 
                      ? 'active sidebar-item sub-sidebar-item' 
                      : 'link sidebar-item sub-sidebar-item'
                  }
                >
                  <Typography
                    component="div"
                    className="sidebar-text" 
                    sx={sidebarStyles.sidebarText(true)}
                  >
                    Beer Cooling
                  </Typography>
                </NavLink>

                {/* Beer Pasteurizer link */}
                <NavLink
                  to={buildProductDesignUrl('beer pasteurizer 3 section', 'hygienic')}
                  className={() => 
                    isProductDesignLinkActive(buildProductDesignUrl('beer pasteurizer 3 section', 'hygienic')) 
                      ? 'active sidebar-item sub-sidebar-item' 
                      : 'link sidebar-item sub-sidebar-item'
                  }
                >
                  <Typography
                    component="div"
                    className="sidebar-text" 
                    sx={sidebarStyles.sidebarText(true)}
                  >
                    Beer Pasteurizer
                  </Typography>
                </NavLink>

                {/* Beer Wort Cooler link */}
                <NavLink
                  to={buildProductDesignUrl('Wort Cooler', 'hygienic')}
                  className={() => 
                    isProductDesignLinkActive(buildProductDesignUrl('Wort Cooler', 'hygienic')) 
                      ? 'active sidebar-item sub-sidebar-item' 
                      : 'link sidebar-item sub-sidebar-item'
                  }
                >
                  <Typography
                    component="div"
                    className="sidebar-text" 
                    sx={sidebarStyles.sidebarText(true)}
                  >
                    Beer Wort Cooler
                  </Typography>
                </NavLink>

                {/* Carbonated Soft Drink Cooler link */}
                <NavLink
                  to={buildProductDesignUrl('Carbonated Soft Drink Cooler', 'hygienic')}
                  className={() => 
                    isProductDesignLinkActive(buildProductDesignUrl('Carbonated Soft Drink Cooler', 'hygienic')) 
                      ? 'active sidebar-item sub-sidebar-item' 
                      : 'link sidebar-item sub-sidebar-item'
                  }
                >
                  <Typography
                    component="div"
                    className="sidebar-text" 
                    sx={sidebarStyles.sidebarText(true)}
                  >
                    Carbonated Soft Drink Cooler
                  </Typography>
                </NavLink>

                {/* Milk Reception Cooler link */}
                <NavLink
                  to={buildProductDesignUrl('Milk cooler', 'hygienic')}
                  className={() => 
                    isProductDesignLinkActive(buildProductDesignUrl('Milk cooler', 'hygienic')) 
                      ? 'active sidebar-item sub-sidebar-item' 
                      : 'link sidebar-item sub-sidebar-item'
                  }
                >
                  <Typography
                    component="div"
                    className="sidebar-text" 
                    sx={sidebarStyles.sidebarText(true)}
                  >
                    Milk Reception Cooler
                  </Typography>
                </NavLink>

                {/* Tropical Milk Cooler link */}
                <NavLink
                  to={buildProductDesignUrl('tropical milk cooler', 'hygienic')}
                  className={() => 
                    isProductDesignLinkActive(buildProductDesignUrl('tropical milk cooler', 'hygienic')) 
                      ? 'active sidebar-item sub-sidebar-item' 
                      : 'link sidebar-item sub-sidebar-item'
                  }
                >
                  <Typography
                    component="div"
                    className="sidebar-text" 
                    sx={sidebarStyles.sidebarText(true)}
                  >
                    Milk Tropical Cooler
                  </Typography>
                </NavLink>

                {/* Utility Heater (Hygienic) link */}
                <NavLink
                  to={buildProductDesignUrl('utility heater', 'hygienic')}
                  className={() => 
                    isProductDesignLinkActive(buildProductDesignUrl('utility heater', 'hygienic')) 
                      ? 'active sidebar-item sub-sidebar-item' 
                      : 'link sidebar-item sub-sidebar-item'
                  }
                >
                  <Typography
                    component="div"
                    className="sidebar-text" 
                    sx={sidebarStyles.sidebarText(true)}
                  >
                    Utility Heater
                  </Typography>
                </NavLink>
              </Box>

            </Box>
          </Collapse>
        </Box>

                {/* Industrial expandable section */}
                <Box>
          <Box 
            className="sidebar-item" 
            onClick={handleIndustrialToggle}
            component="div"
            sx={{ cursor: 'pointer' }}
          >
            <Box
              component="img"
              src={IndustrialIcon}
              alt="Industrial"
              className="sidebar-icon"
            />
            <Typography
              component="div"
              className="sidebar-text" 
              sx={sidebarStyles.sidebarText(isOpen)}
            >
              Industrial
            </Typography>
            {isOpen && (
              <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                {isIndustrialExpanded ? (
                  <ExpandLessIcon sx={{ fontSize: '16px', color: '#323130' }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: '16px', color: '#323130' }} />
                )}
              </Box>
            )}
          </Box>

          <Collapse in={isIndustrialExpanded && isOpen}>
            <Box sx={{ backgroundColor: '#fafafa' }}>
              {/* Industrial sub-section */}
              <Box sx={{ paddingBottom: '8px' }}>
                {/* Utility Heater (Industrial) link */}
                <NavLink
                  to={buildProductDesignUrl('utility heater', 'industrial')}
                  className={() => 
                    isProductDesignLinkActive(buildProductDesignUrl('utility heater', 'industrial')) 
                      ? 'active sidebar-item sub-sidebar-item' 
                      : 'link sidebar-item sub-sidebar-item'
                  }
                >
                  <Typography
                    component="div"
                    className="sidebar-text" 
                    sx={sidebarStyles.sidebarText(true)}
                  >
                    Utility Heater
                  </Typography>
                </NavLink>

                {/* Wort Cooler (Industrial) link */}
                <NavLink
                  to={buildProductDesignUrl('Wort Cooler', 'industrial')}
                  className={() => 
                    isProductDesignLinkActive(buildProductDesignUrl('Wort Cooler', 'industrial')) 
                      ? 'active sidebar-item sub-sidebar-item' 
                      : 'link sidebar-item sub-sidebar-item'
                  }
                >
                  <Typography
                    component="div"
                    className="sidebar-text" 
                    sx={sidebarStyles.sidebarText(true)}
                  >
                    Wort Cooler
                  </Typography>
                </NavLink>
              </Box>
            </Box>
          </Collapse>
        </Box>

          {/* Settings link */}
          <NavLink 
            to="/admin"
            className={({ isActive }: { isActive: boolean }) => 
              isActive ? 'active sidebar-item' : 'link sidebar-item'
            }
          >
            <Box
              component="img"
              src={Settings} 
              alt="Settings" 
              className="sidebar-icon"
            />
            <Typography
              component="div"
              className="sidebar-text" 
              sx={sidebarStyles.sidebarText(isOpen)}
            >
              Settings
            </Typography>
          </NavLink>
        </Box>

        {/* Feedback link - fixed at bottom, not scrollable */}
        <NavLink 
          to="#" 
          onClick={handleFeedbackClick}
          className={() => 'link sidebar-item'}
          style={{ flexShrink: 0 }}
        >
          <Box
            component="img"
            src={feedback} 
            alt="Feedback" 
            className="sidebar-icon"
          />
          <Typography
            component="div"
            className="sidebar-text" 
            sx={sidebarStyles.sidebarText(isOpen)}
          >
            Feedback
          </Typography>
        </NavLink>
      </Stack>
    </>
  );
};